import { DateTime } from 'luxon';
import { CohortMetadata } from 'schemas/metadata/cohorts';

const VERSION_SUFFIX_REGEX = /_v\d+$/;

export const getPrefix = (label: string, regex: RegExp) => label.split(regex)[0].trim();

export const getVersion = (label: string) => parseInt(label.match(VERSION_SUFFIX_REGEX)?.[0].replace('_v', '') || '0', 10);
export const applyAgeFilter = (
  cohorts: CohortMetadata[],
  maxAge: number,
): CohortMetadata[] => {
  if (!Number.isNaN(maxAge)) {
    const cutoff = DateTime.now()
      .minus({ months: maxAge })
      .startOf('day')
      .toFormat('yyyyMMddHH');

    return [...cohorts].filter(
      (cohort) => !cohort.LastRefreshed || cohort.LastRefreshed >= cutoff,
    );
  }

  return [...cohorts];
};

export const removeOldCohortVersions = (cohorts: CohortMetadata[]): CohortMetadata[] => {
  const newCohorts = [...cohorts];

  return Object.values(
    newCohorts.reduce((groups, item) => {
      const prefix = getPrefix(item.CohortLabel, VERSION_SUFFIX_REGEX);
      const currentVersion = getVersion(item.CohortLabel || '0');
      const storedVersion = getVersion(groups[prefix]?.CohortLabel || '0');

      // If the cohort is not in the groups or the current version is greater than the stored version
      if (!groups[prefix] || currentVersion > storedVersion) {
        return { ...groups, [prefix]: item };
      }

      return groups;
    }, {} as Record<string, CohortMetadata>),
  );
};

const normaliseString = (str: string): string => str.toLowerCase().replace(/_/g, '');

export const applyWordsFilter = (
  cohorts: CohortMetadata[],
  filterArray: string[],
): CohortMetadata[] => {
  const newCohorts = [...cohorts];

  return newCohorts.filter((cohort) => filterArray.every((filter) => {
    const normalisedCohortLabel = normaliseString(cohort.CohortLabel);
    const normalisedFilter = normaliseString(filter);

    if (filter.startsWith('-')) {
      return !normalisedCohortLabel.includes(normalisedFilter.slice(1));
    }

    return (
      normalisedCohortLabel === normalisedFilter
      || normalisedCohortLabel.includes(normalisedFilter)
    );
  }));
};

export const getFilteredCohorts = (
  params: {
    cohorts: CohortMetadata[],
    maxAge: number,
    latestVersion: boolean,
    wordsFilter: string
  },
): CohortMetadata[] => {
  const {
    cohorts, maxAge, latestVersion, wordsFilter,
  } = params;

  let filteredCohorts = applyAgeFilter(cohorts, maxAge);

  if (latestVersion) {
    filteredCohorts = removeOldCohortVersions(filteredCohorts);
  }

  if (wordsFilter && wordsFilter.trim().length > 0 && wordsFilter.trim() !== '-') {
    const filterArray = wordsFilter.split(' ');
    filteredCohorts = applyWordsFilter(filteredCohorts, filterArray);
  }

  return filteredCohorts;
};

export const getCohortStatus = (cohort: string, cohorts: CohortMetadata[]): 'removed from team'|'provisional'|'ok' => {
  const foundCohort = cohorts.find((c) => c.CohortLabel === cohort);

  if (!foundCohort) {
    return 'removed from team';
  }

  if (foundCohort.Status === 'provisional') {
    return 'provisional';
  }

  return 'ok';
};

export const getBackToUrl = (
  searchParams: URLSearchParams,
  teamId: string,
  campaignId: string,
  iterationId: string,
): string | undefined => {
  if (searchParams.get('mode') === 'new') {
    return `/${teamId}/campaign/${campaignId}/iteration/${iterationId}/key-details`;
  }
  return undefined;
};
